import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileText, Plus, Edit, Copy, Trash2, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { CreateProposalTemplateDialog } from './CreateProposalTemplateDialog';

interface ProposalTemplate {
  id: string;
  name: string;
  description: string;
  category: string; // 'discovery', 'implementation', 'support', 'consulting'
  pricingModel: 'fixed' | 'hourly' | 'hybrid';
  estimatedValue: number;
  timeline: string;
  sections: {
    executiveSummary: string;
    scopeOfWork: string;
    deliverables: string[];
    timeline: string;
    pricing: string;
    assumptions: string[];
    exclusions: string[];
  };
  createdBy: string;
  createdByName: string;
  createdAt: string;
  firmId: string;
  isGlobal?: boolean; // System-wide template vs firm-specific
}

interface ProposalTemplateLibraryProps {
  accessToken: string;
  firmId: string;
  onApplyTemplate?: (template: ProposalTemplate) => void;
}

export function ProposalTemplateLibrary({ accessToken, firmId, onApplyTemplate }: ProposalTemplateLibraryProps) {
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProposalTemplate | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    loadTemplates();
  }, [firmId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      console.log('[ProposalTemplateLibrary] Loading templates...');
      console.log('[ProposalTemplateLibrary] firmId:', firmId);
      console.log('[ProposalTemplateLibrary] accessToken length:', accessToken?.length);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/proposal-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        console.log('[ProposalTemplateLibrary] Loaded templates:', data.templates?.length || 0);
      } else {
        console.error('[ProposalTemplateLibrary] Failed to load proposal templates:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('[ProposalTemplateLibrary] Error response:', errorText);
        toast.error('Failed to load proposal templates');
      }
    } catch (error) {
      console.error('[ProposalTemplateLibrary] Error loading proposal templates:', error);
      toast.error('Failed to load proposal templates');
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (template: ProposalTemplate) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/proposal-templates/${template.id}/clone`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Template cloned successfully');
        loadTemplates();
      } else {
        toast.error('Failed to clone template');
      }
    } catch (error) {
      console.error('Error cloning template:', error);
      toast.error('Failed to clone template');
    }
  };

  const handleDelete = async (template: ProposalTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/proposal-templates/${template.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Template deleted successfully');
        loadTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleEdit = (template: ProposalTemplate) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  };

  const filteredTemplates = categoryFilter === 'all'
    ? templates
    : templates.filter(t => t.category === categoryFilter);

  const categories = [
    { value: 'all', label: 'All Templates' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'implementation', label: 'Implementation' },
    { value: 'support', label: 'Support' },
    { value: 'consulting', label: 'Consulting' },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5" />
                Proposal Template Library
              </CardTitle>
              <CardDescription>
                Pre-built proposal templates for faster deal creation
              </CardDescription>
            </div>
            <Button onClick={() => {
              setEditingTemplate(undefined);
              setDialogOpen(true);
            }}>
              <Plus className="size-4 mr-2" />
              Create Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category Filter */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {categories.map(cat => (
              <Button
                key={cat.value}
                variant={categoryFilter === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter(cat.value)}
              >
                {cat.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 animate-spin text-gray-400" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="size-12 text-gray-300 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {categoryFilter === 'all' 
                  ? 'No proposal templates yet'
                  : `No ${categoryFilter} templates yet`
                }
              </p>
              <Button onClick={() => {
                setEditingTemplate(undefined);
                setDialogOpen(true);
              }}>
                <Plus className="size-4 mr-2" />
                Create Your First Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map(template => (
                <Card key={template.id} className="relative">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      {template.isGlobal && (
                        <Badge variant="secondary" className="shrink-0">System</Badge>
                      )}
                    </div>
                    <CardDescription className="line-clamp-2">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Category:</span>
                        <p className="capitalize">{template.category}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Pricing:</span>
                        <p className="capitalize">{template.pricingModel}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Est. Value:</span>
                        <p>${template.estimatedValue.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Timeline:</span>
                        <p>{template.timeline}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="default"
                        className="flex-1"
                        onClick={() => onApplyTemplate?.(template)}
                      >
                        Use Template
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(template)}
                      >
                        <Edit className="size-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleClone(template)}
                      >
                        <Copy className="size-4" />
                      </Button>
                      {!template.isGlobal && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(template)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateProposalTemplateDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingTemplate(undefined);
        }}
        onSuccess={() => {
          loadTemplates();
          setDialogOpen(false);
          setEditingTemplate(undefined);
        }}
        accessToken={accessToken}
        firmId={firmId}
        editingTemplate={editingTemplate}
      />
    </>
  );
}